import React, { useState, useEffect } from 'react';
import { useCurrency } from '../../hooks/useCurrency';
import { Home, Plus, Search, Download, Printer } from 'lucide-react';
import { supabase } from '../../lib/supabase';

interface DebitVoucher {
  id: string;
  voucher_number: string;
  voucher_date: string;
  voucher_head: string;
  amount: number;
  payment_method: string;
  reference_number?: string;
  description?: string;
  created_by: string;
  status: string;
}

export default function DebitVoucher() {
  const { formatCurrency } = useCurrency();
  const [vouchers, setVouchers] = useState<DebitVoucher[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    voucher_head: '',
    amount: '',
    payment_method: 'cash',
    reference_number: '',
    description: '',
  });

  useEffect(() => {
    loadDebitVouchers();
  }, [startDate, endDate]);

  const loadDebitVouchers = async () => {
    try {
      setLoading(true);
      
      const url = import.meta.env.VITE_SUPABASE_URL;
      if (!url || url === 'https://placeholder.supabase.co') {
        setVouchers(getMockDebitVouchers());
        setLoading(false);
        return;
      }

      let query = supabase
        .from('vouchers')
        .select('*')
        .eq('voucher_type', 'debit');

      if (startDate) {
        query = query.gte('voucher_date', startDate);
      }
      if (endDate) {
        query = query.lte('voucher_date', endDate);
      }

      const { data, error } = await query.order('voucher_date', { ascending: false });
      if (error) throw error;
      setVouchers(data || []);
    } catch (error) {
      console.error('Error loading debit vouchers:', error);
      setVouchers(getMockDebitVouchers());
    } finally {
      setLoading(false);
    }
  };

  const getMockDebitVouchers = (): DebitVoucher[] => {
    return [
      {
        id: '1',
        voucher_number: 'DV-2025-001',
        voucher_date: '2025-01-27',
        voucher_head: 'Medical Supplies',
        amount: 50000,
        payment_method: 'cash',
        reference_number: 'REF-001',
        description: 'Purchase of medical supplies',
        created_by: 'Admin',
        status: 'approved',
      },
      {
        id: '2',
        voucher_number: 'DV-2025-002',
        voucher_date: '2025-01-26',
        voucher_head: 'Utility Bills',
        amount: 15000,
        payment_method: 'bank_transfer',
        reference_number: 'REF-002',
        description: 'Electricity bill payment',
        created_by: 'Admin',
        status: 'approved',
      },
      {
        id: '3',
        voucher_number: 'DV-2025-003',
        voucher_date: '2025-01-25',
        voucher_head: 'Staff Salary',
        amount: 120000,
        payment_method: 'bank_transfer',
        reference_number: 'REF-003',
        description: 'Monthly salary payment',
        created_by: 'Admin',
        status: 'pending',
      },
    ];
  };

  const filteredVouchers = vouchers.filter(voucher => {
    const matchesSearch = 
      voucher.voucher_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
      voucher.voucher_head.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (voucher.description && voucher.description.toLowerCase().includes(searchTerm.toLowerCase()));
    return matchesSearch;
  });

  const totalAmount = filteredVouchers.reduce((sum, voucher) => sum + voucher.amount, 0);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      const url = import.meta.env.VITE_SUPABASE_URL;
      if (!url || url === 'https://placeholder.supabase.co') {
        alert('Debit voucher created successfully (mock mode)');
        setShowForm(false);
        setFormData({
          voucher_head: '',
          amount: '',
          payment_method: 'cash',
          reference_number: '',
          description: '',
        });
        return;
      }

      const { error } = await supabase
        .from('vouchers')
        .insert([{
          ...formData,
          voucher_type: 'debit',
          voucher_date: new Date().toISOString().split('T')[0],
          status: 'pending',
        }]);
      
      if (error) throw error;

      await loadDebitVouchers();
      setShowForm(false);
      setFormData({
        voucher_head: '',
        amount: '',
        payment_method: 'cash',
        reference_number: '',
        description: '',
      });
    } catch (error) {
      console.error('Error creating debit voucher:', error);
      alert('Error creating debit voucher');
    }
  };

  const handleExportCSV = () => {
    const headers = ['Voucher #', 'Date', 'Head', 'Amount', 'Payment Method', 'Reference', 'Status'];
    const rows = filteredVouchers.map(voucher => [
      voucher.voucher_number,
      voucher.voucher_date,
      voucher.voucher_head,
      voucher.amount,
      voucher.payment_method,
      voucher.reference_number || 'N/A',
      voucher.status,
    ]);

    const csvContent = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `debit-vouchers-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2 text-sm text-gray-600">
            <Home className="w-4 h-4" />
            <span>/</span>
            <span>Billings/Reception</span>
            <span>/</span>
            <span className="text-gray-900 font-medium">Debit Voucher</span>
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={handleExportCSV}
              className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            >
              <Download className="w-4 h-4" />
              <span>Export CSV</span>
            </button>
            <button
              onClick={handlePrint}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Printer className="w-4 h-4" />
              <span>Print</span>
            </button>
            <button
              onClick={() => setShowForm(true)}
              className="flex items-center space-x-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors"
            >
              <Plus className="w-4 h-4" />
              <span>New Voucher</span>
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-none px-6 py-8">
        {showForm && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Create Debit Voucher</h2>
            <form onSubmit={handleSubmit}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Voucher Head *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.voucher_head}
                    onChange={(e) => setFormData({ ...formData, voucher_head: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Amount *
                  </label>
                  <input
                    type="number"
                    required
                    value={formData.amount}
                    onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Payment Method *
                  </label>
                  <select
                    required
                    value={formData.payment_method}
                    onChange={(e) => setFormData({ ...formData, payment_method: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  >
                    <option value="cash">Cash</option>
                    <option value="bank_transfer">Bank Transfer</option>
                    <option value="cheque">Cheque</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Reference Number
                  </label>
                  <input
                    type="text"
                    value={formData.reference_number}
                    onChange={(e) => setFormData({ ...formData, reference_number: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  />
                </div>
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                />
              </div>
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors"
                >
                  Create Voucher
                </button>
              </div>
            </form>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <p className="text-sm text-gray-600">Total Vouchers</p>
            <p className="text-2xl font-bold text-gray-900 mt-1">{filteredVouchers.length}</p>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <p className="text-sm text-gray-600">Total Amount</p>
            <p className="text-2xl font-bold text-red-600 mt-1">{formatCurrency(totalAmount)}</p>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search vouchers..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              />
            </div>
            <div>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                placeholder="Start Date"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              />
            </div>
            <div>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                placeholder="End Date"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Voucher #
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Head
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Amount
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Payment Method
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-4 text-center text-gray-500">
                      Loading...
                    </td>
                  </tr>
                ) : filteredVouchers.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-4 text-center text-gray-500">
                      No debit vouchers found
                    </td>
                  </tr>
                ) : (
                  filteredVouchers.map((voucher) => (
                    <tr key={voucher.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {voucher.voucher_number}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(voucher.voucher_date).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {voucher.voucher_head}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-red-600 font-bold">
                        {formatCurrency(voucher.amount)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                          voucher.payment_method === 'cash' ? 'bg-green-100 text-green-800' :
                          voucher.payment_method === 'bank_transfer' ? 'bg-blue-100 text-blue-800' :
                          'bg-purple-100 text-purple-800'
                        }`}>
                          {voucher.payment_method}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                          voucher.status === 'approved' ? 'bg-green-100 text-green-800' :
                          voucher.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-red-100 text-red-800'
                        }`}>
                          {voucher.status}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
